import { Member } from '@/features/iam/members/Member'
import { type Team } from '@/features/iam/teams/Team'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { collection } from '@/infra/collection'
import { Model, type ModelProps } from '@/infra/model'
import { LegendVersionId } from '@/features/jobs/legends/PublishedLegend'
import { type Conversation } from '@/features/messaging/Conversation'
import { action, computed, observable } from 'mobx'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import { isNotNil } from '@/utils/object-util'
import { type User } from '@/features/iam/users/User'
import {
  Consultation,
  type ConsultationProps,
} from '@/features/jobs/consultations/Consultation'
import {
  type JobType,
  type PrepAndReviewLinkType,
} from '@/features/jobs/job-type'
import { type JobStatus } from '@/features/jobs/job-status'

/**
 * The service a job's legend sells.
 */
export type JobLegendServiceType =
  'Tax' | 'Bookkeeping' | 'Consultation' | 'Misc' | 'Unspecified'

/**
 * How a job was closed: completed (`Done`), `Cancelled`, or created for
 * `Test` purposes.
 */
export type Resolution = 'Done' | 'Cancelled' | 'Test'

/**
 * Default rating configuration.
 */
const RatingConfig = {
  default: {
    platformRatingEnabled: false,
    proRatingEnabled: false,
    customerRatingMethod: 'Stars',
  } as RatingConfig,
}

/**
 * How the job is billed: through Stripe or outside the platform.
 */
export type JobBillingType = 'Stripe' | 'External'

/**
 * Whether a collaborator works on the client or the pro side.
 */
export type CollaboratorType = 'Client' | 'Pro'
/**
 * Whether a collaborator owns the job or supports its owner.
 */
export type CollaboratorRole = 'Champion' | 'Supporting'

/**
 * Where a job's final return amounts came from.
 */
export type JobReturnAmountsSource = 'Extracted' | 'Manual' | 'Unspecified'

/**
 * A state return amount for one tax year.
 */
export interface JobReturnStateAmount {
  state: string
  amount: number
  source: JobReturnAmountsSource
}

/**
 * The federal and state return amounts for one tax year.
 */
export interface JobReturnAmountsYear {
  taxYear: string
  federalAmount?: number
  stateAmounts: JobReturnStateAmount[]
  federalSource: JobReturnAmountsSource
}

/**
 * An answer to one of the job's questions.
 */
export interface JobAnswer {
  id: Id
  answer: unknown
  type?: string
}

/**
 * A user referenced by a job, as the job carries it.
 */
export interface JobUser {
  userId: string
  userPublicId: string
  displayName: string
  email: string
  familyName: string
  givenName: string
  picture: string
  referrerMeta?: string
}

/**
 * Props for constructing a {@link Job}.
 */
export interface JobProps extends ModelProps<Job> {
  collaborators?: CollaboratorProps[]
  consultation?: Partial<ConsultationProps> | null
  legendPriceUnit?: string | null
  billingEnabled?: boolean
  total?: number
  paidAmount?: number
}

/**
 * A job: the unit of work a client buys and a pro delivers.
 */
export class Job extends Model<Job> {
  static readonly none = new Job()

  @observable private accessor _legendPriceUnit: string | null = null
  @observable private accessor _billingEnabled = false
  @observable private accessor _total = 0
  @observable private accessor _paidAmount = 0
  @observable private accessor _consultation: Consultation | null = null

  id = Id.none

  @observable accessor primaryAmount = 0
  @observable accessor providerCutAmount = 0
  @observable accessor stripeCharges: string[] = []
  @observable accessor stripeRefunds: string[] = []
  @observable accessor amendments: Amendment[] = []
  @observable accessor resolution: Resolution | null | undefined = null
  @observable accessor deferPaymentInfoCollection = false
  @observable accessor dateDeadline: Date | null = null
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor dateSubmitted: Date | null = null
  @observable accessor dateAccepted: Date | null = null
  @observable accessor dateClosed: Date | null = null
  @observable accessor dateAdvanceAllowed: Date | null = null
  @observable accessor dateLastActivity = unixEpoch()
  @observable accessor jobScopeItems: JobScopeItem[] = []
  @observable accessor legendId = Id.none
  @observable accessor legendName = ''
  @observable accessor legendVersionId = LegendVersionId.none
  @observable accessor legendServiceType: JobLegendServiceType = 'Unspecified'

  @observable accessor name = ''
  @observable accessor description = ''
  @observable accessor shortId = ''
  @observable accessor couponCodes = ''
  @observable accessor couponAmount = 0
  @observable accessor status: JobStatus = 'UnderConstruction'
  @observable accessor workspace = Workspace.none
  @observable accessor conversation: Conversation | null = null
  @observable accessor allowScheduling = false
  @observable accessor allowProSelection = true
  @observable accessor alwaysEnableVoiceCall = false
  @observable accessor ownerTeam: Team | null = null
  @observable accessor teams: Team[] = []
  @observable accessor infoLines: InfoLine[] = []
  /**
   * Per-tax-year final return amounts. Signed: positive is a refund to the
   * client, negative is owed.
   */
  @observable accessor returnAmounts: JobReturnAmountsYear[] = []
  @observable accessor requestedProId: Id | null = null
  @observable accessor requestedProPublicId: Id | null = null
  @observable accessor requestedPro: Member | null = null
  @observable accessor paymentInfoStatus: PaymentInfoStatus = 'NotProvided'
  @observable.shallow accessor answers: JobAnswer[] = []
  @observable.shallow accessor ratingConfig: RatingConfig = RatingConfig.default
  @observable accessor waitingOn: WaitingOn | null = null
  @observable accessor payoutSplit: PayoutSplit | null = null
  @observable accessor currentUserAllocation: number | null = null
  @observable accessor billingType: JobBillingType = 'Stripe'
  @observable accessor jobType: JobType = { type: 'Unspecified' }

  /**
   * Collaborators collection.
   */
  readonly collaborators = collection({
    getModelId: (m) => m.member.user.publicId,
    getDataId: (d) => d.member?.user.publicId,
    create: () => new Collaborator(this),
  })

  /**
   * The legend's price unit, or `null` for dollars.
   */
  @computed
  get legendPriceUnit() {
    return this._legendPriceUnit
  }

  /**
   * Whether the job is a draft.
   */
  @computed
  get isDraft() {
    return this.status === 'UnderConstruction'
  }

  /**
   * Whether final return amounts were recorded for the job.
   */
  @computed
  get hasReturnAmounts() {
    return this.returnAmounts.length > 0
  }

  /**
   * The scope item that holds the job's bundle, if any.
   */
  @computed
  get jobBundle() {
    return this.jobScopeItems.find((item) => item.bundleId)
  }

  /**
   * The job's consultation, if it has one.
   */
  @computed
  get consultation() {
    return this._consultation
  }

  /**
   * The job's total price.
   */
  @computed
  get total() {
    return this._total
  }

  /**
   * The amount paid so far.
   */
  @computed
  get paidAmount() {
    return this._paidAmount
  }

  /**
   * Whether the job has a consultation that is neither cancelled nor done.
   */
  @computed
  get hasActiveConsultation() {
    return isNotNil(this.consultation)
      ? this.consultation.status !== 'Cancelled' &&
          this.consultation.status !== 'Done'
      : false
  }

  /**
   * The amount that is still due.
   */
  @computed
  get balance() {
    return this.total - this.paidAmount
  }

  /**
   * Display name.
   */
  @computed
  get displayName() {
    return this.name || this.legendName
  }

  /**
   * Whether we can remove clients as a collaborator on the job
   */
  @computed
  get canRemoveClientsAsCollaborator() {
    if (this.supportingClients.length === 0) {
      return false
    }

    return Boolean(this.championClient?.member.isCurrentUser)
  }

  /**
   * Whether billing is enabled for the job.
   */
  @computed
  get isBillingEnabled() {
    return this.workspace.config.billing.enabled && this._billingEnabled
  }

  /**
   * The balance status.
   */
  @computed
  get balanceStatus(): BalanceStatus {
    // Derived client-side because the API doesn't return it.
    if (this._paidAmount > this._total) {
      return 'BalanceNegative'
    }

    if (this._paidAmount < this._total) {
      return 'BalancePositive'
    }

    return 'BalanceZero'
  }

  /**
   * Whether the current user is a Provider-role collaborator.
   */
  @computed
  get isProviderCollaborator() {
    return this.collaborators.some(
      (c) => c.type === 'Pro' && c.member.isCurrentUser,
    )
  }

  /**
   * Whether the current user is a champion Client-role collaborator.
   */
  @computed
  get isChampionClientCollaborator() {
    return this.collaborators.some(
      (c) =>
        c.type === 'Client' && c.role === 'Champion' && c.member.isCurrentUser,
    )
  }

  /**
   * Whether the current user is a supporting Client-role collaborator.
   */
  @computed
  get isSupportingClientCollaborator() {
    return this.collaborators.some(
      (c) =>
        c.type === 'Client' &&
        c.role === 'Supporting' &&
        c.member.isCurrentUser,
    )
  }

  /**
   * Whether job scope change requests are enabled in the workspace.
   */
  @computed
  get isJobScopeChangeRequestsEnabled() {
    return this.workspace.config.combinedPricing.jobScopeChangeRequestsEnabled
  }

  /**
   * Collaborators that are not the current user.
   */
  @computed
  get otherCollaborators() {
    return this.collaborators.filter((c) => !c.member.isCurrentUser)
  }

  /**
   * Supporting Pros on the Job.
   */
  @computed
  get supportingPros() {
    return this.collaborators.filter(
      (c) => c.role === 'Supporting' && c.type === 'Pro',
    )
  }

  /**
   * Supporting Clients on the Job.
   */
  @computed
  get supportingClients() {
    return this.collaborators.filter(
      (c) => c.role === 'Supporting' && c.type === 'Client',
    )
  }

  /**
   * Collaborator entry for the current user.
   */
  @computed
  get currentUserCollaborator() {
    return this.collaborators.find((c) => c.member.isCurrentUser) ?? null
  }

  /**
   * The Client Champion of the job.
   */
  @computed
  get owner() {
    const c = this.collaborators.find(
      (c) => c.type === 'Client' && c.role === 'Champion',
    )
    if (!c) {
      return Member.none
    }
    return c.member
  }

  /**
   * If the job is a client team job.
   */
  @computed
  get isClientTeamJob() {
    return Boolean(this.ownerTeam)
  }

  /**
   * Completed status of the job.
   */
  @computed
  get completed() {
    return this.status === 'Closed'
  }

  /**
   * The Champion Provider of the job.
   */
  @computed
  get championProvider() {
    const provider = this.collaborators.find(
      (c) => c.role === 'Champion' && c.type === 'Pro',
    )

    if (!provider) {
      return null
    }

    return provider
  }

  /**
   * The champion Client collaborator, if there is one.
   */
  @computed
  get championClient() {
    const client = this.collaborators.find(
      (c) => c.role === 'Champion' && c.type === 'Client',
    )

    if (!client) {
      return null
    }

    return client
  }

  /**
   * Whether the job is on hold.
   */
  @computed
  get isOnHold() {
    return this.status === 'OnHold'
  }

  /**
   * Whether the job is waiting to be advanced.
   */
  @computed
  get isPending() {
    return this.status === 'Pending'
  }

  /**
   * Whether a champion Pro is working on the job.
   */
  @computed
  get isClaimedJob() {
    return (
      Boolean(
        this.collaborators.find(
          (member) => member.type === 'Pro' && member.role === 'Champion',
        ),
      ) &&
      this.status !== 'Closed' &&
      this.status !== 'UnderConstruction' &&
      this.status !== 'OnHold'
    )
  }

  /**
   * Whether the user can download the binder.
   */
  @computed
  get canDownloadBinder() {
    return this.workspace.config.sureprep.enabled && !this.isDraft
  }

  /**
   * Returns the provider champion, if there is one.
   */
  @computed
  get proChampion() {
    const proChampion = this.collaborators.find(
      (member) => member.type === 'Pro' && member.role === 'Champion',
    )
    return proChampion
  }

  /**
   * Returns the name of the client team (if there is one) on the job.
   */
  @computed
  get clientTeamName() {
    return this.ownerTeam?.name
  }

  /**
   * Returns the name of the provider team (if there is one) on the job.
   */
  @computed
  get proTeamName() {
    return this.proChampion?.team?.name
  }

  /**
   * Indicate if the job is part of the prep and review jobs
   */
  @computed
  get containsPrepAndReviewLink() {
    return (
      this.jobType.type === 'Tax' && this.jobType.jobLinks?.prepAndReviewLink
    )
  }

  /**
   * Returns the job tax type for this job.
   */
  @computed
  get prepAndReviewJobText(): PrepAndReviewLinkType {
    return this.prepAndReviewJobLinkType === 'Prep' ? 'Review' : 'Prep'
  }

  /**
   * Returns the id of the sibling job in the prep and review
   */
  @computed
  get prepAndReviewJobLinkId() {
    if (this.jobType.type !== 'Tax') {
      return null
    }
    return this.jobType.jobLinks?.prepAndReviewLink.id
  }

  /**
   * Returns the jobType of the sibling job in the prep and review
   */
  @computed
  get prepAndReviewJobLinkType() {
    if (this.jobType.type !== 'Tax') {
      return null
    }
    return this.jobType.jobLinks?.prepAndReviewLink.type
  }

  /**
   * Sets the modified date.
   *
   * @param date
   */
  @action.bound
  setModified(date: Date) {
    this.dateModified = date
  }

  @action.bound
  override set({
    collaborators,
    consultation,
    legendPriceUnit,
    billingEnabled,
    total,
    paidAmount,
    dateModified,
    ...props
  }: Partial<JobProps>): this {
    if (legendPriceUnit !== undefined) {
      this._legendPriceUnit = legendPriceUnit
    }
    if (billingEnabled !== undefined) {
      this._billingEnabled = billingEnabled
    }
    if (total !== undefined) {
      this._total = total
    }
    if (paidAmount !== undefined) {
      this._paidAmount = paidAmount
    }

    this.collaborators.reset(collaborators)

    if (consultation) {
      if (this._consultation) {
        this._consultation.set(consultation)
      } else {
        this._consultation = new Consultation().set(consultation)
      }
    } else if (consultation === null) {
      this._consultation = null
    }

    // Realtime events may arrive out of order, so never move the modified
    // date backwards.
    if (dateModified && dateModified > this.dateModified) {
      this.dateModified = dateModified
    }

    return super.set(props)
  }
}

/**
 * Amendment.
 */
export interface Amendment {
  readonly creator: JobUser
  readonly customerAmount: number
  readonly dateCreated: Date
  readonly description: string
  readonly internalNote: string
  readonly providerAmount: number
  readonly visibility: AmendmentVisibility
}

/**
 * An item in the job's scope.
 */
export interface JobScopeItem {
  readonly id: Id
  readonly name: string
  readonly description: string
  readonly quantity: number
  readonly courtesyQuantity?: number
  readonly inventoryItemId?: Id
  readonly inventoryItemVersionId?: Id
  readonly price?: number
  readonly proPrice?: number
  readonly bundleId: Id | null
  readonly customItemId: Id | null
}

/**
 * Rating configuration
 */
export interface RatingConfig {
  platformRatingEnabled: boolean
  proRatingEnabled: boolean
  customerRatingMethod: RatingMethod
}

/**
 * How clients rate the job.
 */
export type RatingMethod = 'Thumbs' | 'Stars'

/**
 * Info Line.
 */
export interface InfoLine {
  title: string
  content: string
}

export type CollaboratorProps = ModelProps<Collaborator>

/**
 * A client or pro collaborating on a job.
 */
export class Collaborator extends Model<Collaborator> {
  @observable accessor type: CollaboratorType = 'Client'
  @observable accessor role: CollaboratorRole = 'Champion'
  @observable accessor member: Member = Member.none
  @observable accessor team: Team | null = null

  constructor(readonly job: Job) {
    super()
  }

  /**
   * Checks if the collaborator is what we are asking.
   *
   * @param type The type. If omitted, only checks role.
   * @param role The role. If omitted, only checks type.
   */
  is(type: CollaboratorType | null, role?: CollaboratorRole | null) {
    const matchesType = !type || type === this.type
    const matchesRole = !role || role === this.role
    return matchesType && matchesRole
  }
}

/**
 * `BalanceZero`: we are neither owed nor owe money. `BalancePositive`: the user
 * owes us money. `BalanceNegative`: the user has paid too much and should
 * receive a refund.
 */
export type BalanceStatus =
  'BalanceZero' | 'BalancePositive' | 'BalanceNegative'

/**
 * Whether payment info was requested from, or provided by, the client.
 */
export type PaymentInfoStatus = 'NotProvided' | 'Requested' | 'Provided'

/**
 * Which side of the job has to act next.
 */
export type WaitingOn = 'Provider' | 'Client'

/**
 * How the job's payout is split between pros.
 */
export interface PayoutSplit {
  /**
   * The kind of payout split situation we are finding ourselves in.
   */
  readonly situation: PayoutSplitSituation
  /**
   * The payout allocation list.
   */
  readonly allocations: Allocation[]
}

/**
 * Describes the payout situation.
 */
export type PayoutSplitSituation =
  DefaultPayoutSplit | ManualPayoutSplit | AutoPayoutSplit

/**
 * Run-of-the-mill bog-standard payout, has not been messed with in any way
 * worth writing home about. Basically, it was neither manually split, nor has it
 * been auto-adjusted.
 */
export interface DefaultPayoutSplit {
  readonly type: 'Default'
}

/**
 * The payout split has been manually adjusted.
 */
export interface ManualPayoutSplit {
  readonly type: 'Manual'
  /**
   * The user that manually adjusted the split.
   */
  readonly allocatedBy: User
  /**
   * The time that the split was manually adjusted.
   */
  readonly allocatedAt: Date
}

/**
 * The split was auto-adjusted by the system.
 */
export interface AutoPayoutSplit {
  readonly type: 'Auto'
  /**
   * The time that the split was auto-adjusted.
   */
  readonly adjustedAt: Date
}

export const PayoutSplit = {
  /**
   * Empty split.
   */
  empty(): PayoutSplit {
    return { allocations: [], situation: { type: 'Default' } }
  },
} as const

/**
 * A pro's share of the job's payout.
 */
export interface Allocation {
  /**
   * The user that the amount is allocated for.
   */
  user: User
  /**
   * The amount allocated to the user.
   */
  amount: number
  /**
   * The amount that is authorized for partial payout to the user.
   */
  authorizedPartialPayoutAmount: number
}

/**
 * Who can see an amendment.
 */
export type AmendmentVisibility = 'Public' | 'Internal'
